import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";

import {
  enrollPasskey,
  passkeyPromptStorageKey,
} from "@util/passkeyEnroll";
import { isWebAuthnAvailable } from "@util/passkeys";
import { suggestedPasskeyNickname } from "@util/suggestedPasskeyNickname";
import useAppStore from "@duck/app-store";

export default function PasskeyEnrollPrompt() {
  const admin = useAppStore((state) => state.admin);
  const user = useAppStore((state) => state.user);
  const actor = admin || user;

  const [opened, setOpened] = useState(false);
  const [naming, setNaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nickname, setNickname] = useState(() => suggestedPasskeyNickname());
  const nicknameReady = nickname.trim().length > 0;

  useEffect(() => {
    if (!actor || !isWebAuthnAvailable()) return;

    const storageKey = passkeyPromptStorageKey(actor);
    if (!storageKey) return;
    if (sessionStorage.getItem(storageKey) === "1") return;

    let cancelled = false;

    axios
      .post("/retrieve-passkeys/", {})
      .then(({ data }) => {
        if (cancelled) return;
        const passkeys = data.response || [];
        if (passkeys.length === 0) {
          setOpened(true);
        }
      })
      .catch(() => {
        // Stay quiet on boot — profile still has manual enroll.
      });

    return () => {
      cancelled = true;
    };
  }, [actor]);

  function dismiss() {
    const storageKey = passkeyPromptStorageKey(actor);
    if (storageKey) {
      sessionStorage.setItem(storageKey, "1");
    }
    setNaming(false);
    setOpened(false);
  }

  function onStartAdd() {
    setNickname(suggestedPasskeyNickname());
    setNaming(true);
  }

  function onBack() {
    setNaming(false);
  }

  async function onCreatePasskey() {
    if (!nicknameReady) {
      toast.error(
        "Give this passkey a nickname that describes the device, like My iPhone."
      );
      return;
    }

    setLoading(true);
    try {
      await enrollPasskey(nickname);
      toast.success("Passkey added. You can use it next time you sign in.");
      setNickname(suggestedPasskeyNickname());
      setNaming(false);
      dismiss();
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.error("Passkey prompt was cancelled.");
      } else {
        toast.error(typeof err === "string" ? err : err?.message || err);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={dismiss}
      title="Sign in faster next time"
      centered
    >
      <Stack gap="md">
        <Text size="sm">
          Add a passkey to use Face ID, Touch ID, or your device lock instead of
          a password.
        </Text>
        {naming ? (
          <>
            <TextInput
              label="Device nickname"
              description="Suggested from this browser — edit if you want something clearer, e.g. Work MacBook."
              placeholder={suggestedPasskeyNickname() || "My iPhone"}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={loading}
              required
            />
            <Group justify="flex-end">
              <Button
                variant="subtle"
                color="gray"
                onClick={onBack}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                onClick={onCreatePasskey}
                loading={loading}
                disabled={!nicknameReady}
              >
                Create passkey
              </Button>
            </Group>
          </>
        ) : (
          <Group justify="flex-end">
            <Button
              variant="subtle"
              color="gray"
              onClick={dismiss}
              disabled={loading}
            >
              Not now
            </Button>
            <Button onClick={onStartAdd} disabled={loading}>
              Add passkey
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
